<script>

    import { Loader } from '@googlemaps/js-api-loader'
    import { createEventDispatcher, onDestroy, onMount } from 'svelte'

    const { GOOGLE_MAPS_KEY } = _CONSTANTS_
    const dispatch = createEventDispatcher()
    const MIN_VERTICES = 3

    export let zones = []
    export let hiddenId = null
    export let interactive = true
    export let color = '#098e37'

    let mapEl
    let map
    let mapError = false
    let saved = []
    let fitted = false

    let points = []
    let cursor = null
    let preview = null
    let firstMarker = null
    let draft = null
    let listeners = []

    onMount(() => {
        if (!GOOGLE_MAPS_KEY || !mapEl) return

        const loader = new Loader({
            apiKey: GOOGLE_MAPS_KEY,
            version: 'weekly',
        })

        loader.load().then(() => {
            map = new google.maps.Map(mapEl, {
                center: { lat: 19.4326, lng: -99.1332 },
                zoom: 11,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                disableDoubleClickZoom: true,
            })
            dispatch('ready')
        }).catch(() => {
            mapError = true
        })
    })

    onDestroy(() => {
        removeListeners()
    })

    $: if (map)
        drawSaved(zones, hiddenId, interactive)

    $: if (draft)
        draft.setOptions({ fillColor: color, strokeColor: color })

    $: if (preview)
        preview.setOptions({ strokeColor: color })

    function drawSaved() {
        saved.forEach(item => item.setMap(null))
        saved = []

        ;(zones || []).forEach(zone => {
            if (String(zone._id) === String(hiddenId)) return

            const polygon = new google.maps.Polygon({
                map,
                paths: zone.path,
                strokeColor: zone.color,
                strokeWeight: 2,
                fillColor: zone.color,
                fillOpacity: 0.25,
                clickable: interactive,
                zIndex: 1,
            })
            polygon.addListener('click', () => dispatch('select', zone))
            saved.push(polygon)

            saved.push(new google.maps.Marker({
                map,
                position: boundsOf(zone.path).getCenter(),
                clickable: false,
                zIndex: 2,
                icon: { path: google.maps.SymbolPath.CIRCLE, scale: 0 },
                label: {
                    text: zoneLabel(zone),
                    color: '#1a1a1a',
                    fontSize: '12px',
                    fontWeight: '700',
                    className: 'zone-map-label',
                },
            }))
        })

        if (!fitted && (zones || []).length) {
            fitted = true
            fit((zones || []).flatMap(zone => zone.path))
        }
    }

    function zoneLabel(zone) {
        const driver = ((zone.driver && zone.driver.name) || 'Sin driver').trim()
        const name = driver.length > 24 ? `${driver.slice(0, 23)}…` : driver

        return `${zone.code} · ${name}`
    }

    /** Empieza a dibujar un polígono nuevo; cada clic en el mapa agrega un vértice. */
    export function startDrawing() {
        if (!map) return

        clearDraft()
        map.setOptions({ draggableCursor: 'crosshair' })

        preview = new google.maps.Polyline({
            map,
            path: [],
            strokeColor: color,
            strokeWeight: 2,
            clickable: false,
            zIndex: 3,
        })

        listeners.push(map.addListener('click', event => addPoint(event.latLng.toJSON())))
        listeners.push(map.addListener('mousemove', event => {
            cursor = event.latLng.toJSON()
            renderPreview()
        }))
        emitDrawing()
    }

    export function undoPoint() {
        if (!points.length) return

        points = points.slice(0, -1)

        if (!points.length && firstMarker) {
            firstMarker.setMap(null)
            firstMarker = null
        }

        renderPreview()
        emitDrawing()
    }

    /** Cierra el polígono en dibujo y lo deja editable. */
    export function finishDrawing() {
        if (points.length < MIN_VERTICES) return

        editPath(points, false)
    }

    /** Muestra un polígono editable: vértices arrastrables y clic derecho para borrar uno. */
    export function editPath(path, centerOnPath = true) {
        if (!map) return

        const initial = path.map(point => ({ lat: point.lat, lng: point.lng }))

        clearDraft()

        draft = new google.maps.Polygon({
            map,
            paths: initial,
            editable: true,
            draggable: true,
            strokeColor: color,
            strokeWeight: 2,
            fillColor: color,
            fillOpacity: 0.35,
            zIndex: 3,
        })

        const mvc = draft.getPath()
        const emit = () => dispatch('path', { path: draft.getPath().getArray().map(point => point.toJSON()) })

        // Al arrastrar la zona completa, set_at se dispara por cada vértice: se avisa solo al soltar.
        let moving = false
        const emitIfStill = () => {
            if (!moving) emit()
        }

        listeners.push(mvc.addListener('insert_at', emitIfStill))
        listeners.push(mvc.addListener('set_at', emitIfStill))
        listeners.push(mvc.addListener('remove_at', emitIfStill))
        listeners.push(draft.addListener('dragstart', () => {
            moving = true
        }))
        listeners.push(draft.addListener('dragend', () => {
            moving = false
            emit()
        }))
        listeners.push(draft.addListener('rightclick', event => {
            if (event.vertex != null && mvc.getLength() > MIN_VERTICES)
                mvc.removeAt(event.vertex)
        }))

        emit()

        if (centerOnPath)
            fit(initial)
    }

    export function clearDraft() {
        removeListeners()

        if (preview) preview.setMap(null)
        if (firstMarker) firstMarker.setMap(null)
        if (draft) draft.setMap(null)

        preview = null
        firstMarker = null
        draft = null
        points = []
        cursor = null

        if (map) map.setOptions({ draggableCursor: null })
    }

    function addPoint(point) {
        points = [...points, point]

        if (points.length === 1) {
            firstMarker = new google.maps.Marker({
                map,
                position: point,
                zIndex: 4,
                title: 'Cerrar zona',
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 7,
                    fillColor: '#ffffff',
                    fillOpacity: 1,
                    strokeColor: color,
                    strokeWeight: 3,
                },
            })
            firstMarker.addListener('click', () => finishDrawing())
        }

        renderPreview()
        emitDrawing()
    }

    function renderPreview() {
        if (!preview) return

        preview.setPath(cursor && points.length ? [...points, cursor] : points)
    }

    function emitDrawing() {
        dispatch('drawing', { count: points.length })
    }

    function removeListeners() {
        listeners.forEach(listener => listener.remove())
        listeners = []
    }

    function boundsOf(path) {
        const bounds = new google.maps.LatLngBounds()
        path.forEach(point => bounds.extend(point))
        return bounds
    }

    function fit(path) {
        if (!map || !path.length) return

        map.fitBounds(boundsOf(path), 48)
    }

</script>

<style>
    .zone-map {
        position: relative;
        height: 640px;
        border-radius: 28px;
        overflow: hidden;
        background: #f4f4f4;
    }

    .zone-map__canvas {
        width: 100%;
        height: 100%;
    }

    .zone-map__note {
        position: absolute;
        z-index: 1;
        top: 12px;
        left: 12px;
        margin: 0;
        padding: 0.35rem 0.7rem;
        border-radius: 999px;
        background: #fff;
        color: #3a3a3a;
        font-size: 12px;
        font-weight: 600;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    }

    @media (max-width: 900px) {
        .zone-map {
            height: 420px;
            border-radius: 20px;
        }
    }

    :global(.zone-map-label) {
        background: #fff;
        border-radius: 999px;
        padding: 2px 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
        white-space: nowrap;
    }
</style>

<div class="zone-map">
    {#if !GOOGLE_MAPS_KEY}
        <p class="zone-map__note">Falta la llave de Google Maps en el admin</p>
    {:else if mapError}
        <p class="zone-map__note">No se pudo cargar el mapa</p>
    {/if}
    <slot />
    <div class="zone-map__canvas" bind:this={mapEl}></div>
</div>
